// @ts-strict-ignore
import {Component, inject, Injector, OnInit} from "@angular/core";
import {BasicMaterialDialog, MatDialogDataBaseInput} from "../../basic-material-dialog/basic-material-dialog.directive";
import {
	CallingApplication,
	CameraResolutionType,
	CameraViewMode,
	ClientType,
	DeviceActivityType,
	DisplayMode,
	Modules,
	TrainingRoom,
	ViewingStationMapping,
} from "@models";
import {SimClientRegisterService, SimConfigurationService, SimIdleReloadService, SystemHub} from "@services";
import {EventEmitterService} from "@event-emitter";
import {FormControl} from "@angular/forms";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

export interface DialogData_ShowRoomDetailDialogInput extends MatDialogDataBaseInput {
	trainingRoom: TrainingRoom;
	useAudio?: boolean;
	enableControls?: boolean;
	// used for exam associated announcement sessions
	announcementSessionId?: string;
}

@UntilDestroy()
@Component({
	selector: "sim-show-room-detail-dialog",
	templateUrl: "./show-room-detail-dialog.component.html",
	styleUrls: ["./show-room-detail-dialog.component.scss"],
	standalone: false,
})
export class ShowRoomDetailDialogComponent
	extends BasicMaterialDialog<ShowRoomDetailDialogComponent, DialogData_ShowRoomDetailDialogInput>
	implements OnInit
{
	// Properties
	public mapping: ViewingStationMapping = null;
	public useAudio = false;
	public selectedPreset = 0;
	public connectedWebClientId: string;
	public volumeCtrl: FormControl<number> = new FormControl<number>(50);
	public previousVolumeValue: number = 50;

	// Readonly
	protected readonly DisplayMode = DisplayMode;
	protected readonly CameraResolutionType = CameraResolutionType;
	protected readonly CameraViewMode = CameraViewMode;
	protected readonly Modules = Modules;
	protected readonly ClientType = ClientType;
	protected readonly hasLicenseModule = SimConfigurationService.hasLicenseModule;

	// Injections
	private _simClientRegisterService = inject(SimClientRegisterService);
	private _simIdleReloadService = inject(SimIdleReloadService);
	private _emitterService = inject(EventEmitterService);
	private _systemHub = inject(SystemHub);

	constructor(_injector: Injector) {
		super(_injector);
	}

	public async ngOnInit() {
		// Add to SignalR Group
		await this._systemHub.addToGroup(DeviceActivityType.RoomAccess, this.data.trainingRoom.id);

		// Init Audio
		this._initAudio().then();

		// WebClient ID
		this._emitterService.onConnectedWebClientUpsert.subscribe(async (connection) => {
			if (connection.connectedWebClient.executorWebClientInfoId == this.mapping.id) {
				this.connectedWebClientId = connection.connectedWebClient.controllerWebClientInfoId;
			}
		});
	}

	public async ngOnDestroy() {
		await this._systemHub.removeFromGroup(DeviceActivityType.RoomAccess, this.data.trainingRoom.id);
	}

	private async _initAudio(): Promise<void> {
		// Check if relevant
		this.useAudio = this.data.useAudio;
		if (!this.useAudio) {
			return;
		}

		// Get Cached Volume
		const cachedVolume = window.localStorage.getItem("volume");
		if (cachedVolume !== null && cachedVolume !== undefined) {
			this.volumeCtrl.setValue(parseInt(cachedVolume));
		}

		// Get Mapping
		const response = await this._simClientRegisterService.registerViewingStation(CallingApplication.LiveView, null);
		const mapping = response?.mapping;
		if (mapping) {
			this.mapping = mapping;
			this._simIdleReloadService.activate();
		}

		// (Un)Mute Volume
		this.volumeCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
			if (value > 0) {
				this.previousVolumeValue = value;
			}
			window.localStorage.setItem("volume", value.toString());
		});
	}
}
